'use client'

import { UserRoundCheck } from 'lucide-react'

import type { OrderTotals } from '@/lib/order-money'
import { RADIUS, SURFACE } from '@/lib/surface'
import { formatPLN, VAT_RATE, withVat } from '@/lib/tiers'
import { BODY, META, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * The money, and nothing else.
 *
 * This column used to be a second list of the order: its own rows, its own
 * stepper, its own discount badges — over a different set of lines than the
 * cards beside it, because a position only appeared here once it had been
 * "added". The two disagreed constantly. It is now arithmetic over the same
 * lines the reader can see, which is one sentence that removed six ways for the
 * screen to contradict itself.
 *
 * Gross is the headline. Every price in this system is net, so VAT is a real
 * amount somebody pays rather than a footnote, and the ordering screen is the
 * one surface that has to headline what actually leaves the account.
 */
export function OrderTotalCard({
  totals,
  hasLines,
  coordinator,
  onCoordinator,
  onClear,
  className,
}: {
  totals: OrderTotals
  /** The highest bonus this catalogue can actually reach; the nudge is capped to it. */
  hasLines: boolean
  /** The customer's tick. Meaningless outside `OPTIONAL` — see `wantsCoordinator`. */
  coordinator: boolean
  onCoordinator: (next: boolean) => void
  onClear: () => void
  className?: string
}) {
  const net = totals.total
  const gross = withVat(net)
  const vat = gross - net

  return (
    <section
      id="podsumowanie"
      aria-labelledby="podsumowanie-tytul"
      className={cn('scroll-mt-28 p-6', RADIUS.feature, SURFACE, className)}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="podsumowanie-tytul" className="text-[1.05rem] font-bold text-white">
          Twoje zamówienie
        </h2>
        {hasLines ? (
          <button
            type="button"
            onClick={onClear}
            className={cn(MICRO, 'font-medium text-white/55 transition-colors hover:text-white')}
          >
            Wyczyść
          </button>
        ) : null}
      </div>

      {hasLines ? null : (
        <p className={cn(META, 'mt-3 leading-relaxed text-white/55')}>
          Wybierz wariant obok — a przy sprzęcie, studiu i ekipie także termin. Pozycja trafia do
          zamówienia od razu, jeszcze zanim ją dokończysz.
        </p>
      )}

      {/* Spec §13, said where the money is: the amount above the button moves
          the moment the box is ticked, which is the only way an opt-in charge
          is honest. Below two positions there is nothing here at all. */}
      {totals.coordinatorMode === 'OPTIONAL' ? (
        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-2xl border border-white/[0.12] bg-white/[0.03] px-4 py-3 transition-colors hover:border-white/25">
          <input
            type="checkbox"
            checked={coordinator}
            onChange={(e) => onCoordinator(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-white"
          />
          <span className="min-w-0">
            <span className={cn(META, 'flex items-center gap-1.5 font-bold text-white')}>
              <UserRoundCheck size={13} aria-hidden="true" />
              Koordynator — {formatPLN(totals.coordinatorFee)}
            </span>
            <span className={cn(MICRO, 'mt-1 block leading-relaxed text-white/55')}>
              Jedna osoba pilnuje {totals.positions} pozycji: dobiera wykonawców, trzyma terminy i
              odpowiada Ci w jednym miejscu. Bez niej piszesz do każdego z osobna.
            </span>
          </span>
        </label>
      ) : null}

      {totals.coordinatorMode === 'COMPULSORY' ? (
        <p className="mt-4 flex items-start gap-3 rounded-2xl border border-white/[0.12] bg-white/[0.03] px-4 py-3">
          <UserRoundCheck size={13} aria-hidden="true" className="mt-1 shrink-0 text-white/70" />
          <span className="min-w-0">
            <span className={cn(META, 'font-bold text-white')}>Koordynator w cenie</span>
            <span className={cn(MICRO, 'mt-1 block leading-relaxed text-white/55')}>
              Od {totals.positions} pozycji dokładamy go bez dopłaty — przy tej skali nikt nie
              powinien pilnować kilkunastu terminów sam.
            </span>
          </span>
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-2.5 border-t border-white/10 pt-5">
        {totals.coordinatorCharged > 0 ? (
          <div className="flex items-baseline justify-between gap-3">
            <span className={cn(META, 'text-white/60')}>Koordynator</span>
            <span className="text-[0.95rem] font-semibold text-white/90">
              {formatPLN(totals.coordinatorCharged)}
            </span>
          </div>
        ) : null}
        <div className="flex items-baseline justify-between gap-3">
          <span className={cn(META, 'text-white/60')}>Wartość netto</span>
          <span className="text-[0.95rem] font-semibold text-white/90">{formatPLN(net)}</span>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <span className={cn(META, 'text-white/60')}>VAT {Math.round(VAT_RATE * 100)}%</span>
          <span className="text-[0.95rem] font-semibold text-white/90">{formatPLN(vat)}</span>
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3 border-t border-white/10 pt-4">
        <span className={cn(META, 'tracking-[0.2em] text-white/60 uppercase')}>Do zapłaty</span>
        <div className="flex items-baseline gap-1">
          <span className="text-[2rem] leading-none font-bold tracking-tight text-white">
            {formatPLN(gross)}
          </span>
          {gross > 0 ? <span className={cn(BODY, 'text-white/60')}>brutto</span> : null}
        </div>
      </div>

      {/* Said plainly, next to the number it is missing from. */}
      {totals.quoteCount > 0 ? (
        <p className={cn(MICRO, 'mt-2 leading-relaxed text-white/55')}>
          {totals.quoteCount === 1 ? 'Jedna pozycja czeka' : `${totals.quoteCount} pozycje czekają`}{' '}
          na wycenę — przygotujemy ją po briefie, dlatego nie ma jej w sumie powyżej.
        </p>
      ) : null}
      {/* Said next to the number it IS in, for the same reason. */}
      {totals.rentalCount > 0 ? (
        <p className={cn(MICRO, 'mt-2 leading-relaxed text-white/55')}>
          Sprzęt liczymy po cenie z cennika za wybraną długość — kwota powyżej jest pełna.
          Dostępność w wybranym terminie potwierdzamy przed odbiorem.
        </p>
      ) : null}
      {/*
        The closing line, and it has to tell the truth about THIS basket.

        It used to read "Ostateczną kwotę i sposób płatności potwierdzamy po
        briefie" unconditionally — including on a basket that is priced to the
        grosz, where the customer is one click from an order for exactly the
        number printed above. That is not a disclaimer, it is a contradiction:
        the amount IS final, `settlePayment` will take that figure, and the
        invoice will carry it. It also duplicated the precise sentence two
        paragraphs up, which already says which lines are still to be quoted.

        So the caveat is kept for the basket that has one and dropped for the
        basket that does not — where what remains to be said is simply what
        happens next.
      */}
      <p className={cn(MICRO, 'mt-2 leading-relaxed text-white/55')}>
        {totals.quoteCount > 0
          ? 'Ostateczną kwotę potwierdzamy po briefie. Dane do przelewu dostaniesz zaraz po złożeniu zamówienia.'
          : 'Kwota jest ostateczna. Dane do przelewu dostaniesz zaraz po złożeniu zamówienia.'}
      </p>
    </section>
  )
}
